/**
 * Portal + floating utility.
 *
 * Monta un elemento dropdown en document.body con position: fixed y lo
 * posiciona respecto al bounding rect de un anchor. Recalcula en scroll y
 * resize. Devuelve una funcion cleanup que desmonta el panel y remueve
 * los listeners.
 *
 * Uso:
 *   const cleanup = openFloating({
 *     anchor,
 *     panel,
 *     placement: 'bottom-start',
 *     onOutsideClick: () => cleanup(),
 *   });
 *   // ...
 *   cleanup();
 */

export type FloatingPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

export interface FloatingOptions {
  /** Anchor element (trigger) */
  anchor: HTMLElement;
  /** Panel element que sera insertado en document.body */
  panel: HTMLElement;
  /** Placement relativo al anchor (default: bottom-start). */
  placement?: FloatingPlacement;
  /** Offset vertical en px entre anchor y panel (default: 4). */
  offset?: number;
  /** Si true, el panel hereda min-width del anchor (default: true). */
  matchWidth?: boolean;
  /** z-index a usar (default: var(--ui-z-dropdown, 100) + 10). */
  zIndex?: number;
  /** Callback cuando se clickea fuera del anchor+panel. */
  onOutsideClick?: () => void;
  /** Callback cuando se presiona Escape. */
  onEscape?: () => void;
}

export interface FloatingCleanup {
  (): void;
}

export function openFloating(opts: FloatingOptions): FloatingCleanup {
  const {
    anchor,
    panel,
    placement = 'bottom-start',
    offset = 4,
    matchWidth = true,
    // 9500: por encima de ext-modal (9000). Caso tipico: <ext-select> dentro
    // de un <ext-modal>; sin z-index alto el panel del dropdown queda debajo
    // del overlay, los clicks al panel caen sobre el backdrop y cierran el
    // modal. 9500 garantiza que el panel siempre este al frente.
    zIndex = 9500,
    onOutsideClick,
    onEscape,
  } = opts;

  panel.style.position = 'fixed';
  panel.style.zIndex = String(zIndex);
  panel.style.margin = '0';

  // El panel vive en document.body (portal). Sin stopPropagation los mousedown
  // y click dentro del panel burbujean por document y pueden disparar handlers
  // en ancestros que NO ven el panel como descendiente (ej. <ext-modal> cierra
  // al detectar clicks fuera de su dialog). Con stopPropagation contenemos el
  // evento al panel antes de que alcance esos listeners.
  const stopMouse = (ev: Event) => ev.stopPropagation();
  panel.addEventListener('mousedown', stopMouse);
  panel.addEventListener('click', stopMouse);

  const reposition = (): void => {
    const rect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    let top = 0;
    let left = 0;

    const wantsTop = placement.startsWith('top');
    const wantsEnd = placement.endsWith('end');

    if (wantsTop) {
      top = rect.top - panelRect.height - offset;
      if (top < 8) top = rect.bottom + offset; // flip si no entra arriba
    } else {
      top = rect.bottom + offset;
      if (top + panelRect.height > vh - 8 && rect.top - panelRect.height - offset > 8) {
        top = rect.top - panelRect.height - offset;
      }
    }

    if (wantsEnd) {
      left = rect.right - panelRect.width;
    } else {
      left = rect.left;
    }

    // clamp al viewport
    if (left + panelRect.width > vw - 8) left = vw - panelRect.width - 8;
    if (left < 8) left = 8;

    panel.style.top = `${Math.round(top)}px`;
    panel.style.left = `${Math.round(left)}px`;

    if (matchWidth) {
      panel.style.minWidth = `${Math.round(rect.width)}px`;
    }
  };

  const onDocumentClick = (ev: MouseEvent): void => {
    const target = ev.target as Node | null;
    if (!target) return;
    if (anchor.contains(target)) return;
    if (panel.contains(target)) return;
    onOutsideClick?.();
  };

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      onEscape?.();
    }
  };

  const onScroll = (): void => reposition();
  const onResize = (): void => reposition();

  document.body.appendChild(panel);
  // Posicion inicial (antes de medir width real).
  reposition();
  // Segundo pass ahora que el panel esta mounted y tiene dimensiones finales.
  requestAnimationFrame(reposition);

  document.addEventListener('mousedown', onDocumentClick, true);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onResize);

  return (): void => {
    document.removeEventListener('mousedown', onDocumentClick, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
    if (panel.parentElement) {
      panel.parentElement.removeChild(panel);
    }
  };
}
